import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Pagination,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import usePermissions from '../../../hooks/usePermissions';
import {
  SiteRouter,
  SiteRouterInput,
  SiteRouterService,
} from '../../../services/SiteRouterService';

interface RoutersWidgetProps {
  projectId: number;
}
const empty: SiteRouterInput = { serial_number: '', mac_address: '', in_use: true, note: '' };
const message = (error: unknown) =>
  axios.isAxiosError(error)
    ? error.response?.data?.message || '라우터 요청에 실패했습니다.'
    : error instanceof Error
      ? error.message
      : '라우터 요청에 실패했습니다.';

const RouterInventory: React.FC<RoutersWidgetProps> = ({ projectId }) => {
  const { canEdit } = usePermissions();
  const [routers, setRouters] = useState<SiteRouter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<SiteRouter | null | undefined>(undefined);
  const [form, setForm] = useState<SiteRouterInput>(empty);
  const [formError, setFormError] = useState('');
  const [busy, setBusy] = useState(false);
  // 해지(회사 보관으로 이동)와 삭제는 같은 확인 대화상자를 쓴다.
  const [confirming, setConfirming] = useState<{
    router: SiteRouter;
    action: 'release' | 'delete';
  } | null>(null);
  const alive = useRef(true);
  const pending = useRef(false);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    SiteRouterService.list(projectId)
      .then((items) => {
        if (!cancelled) setRouters(items);
      })
      .catch((e) => {
        if (!cancelled) setError(message(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, reload]);

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setFormError('');
    try {
      const item = await SiteRouterService.save(projectId, form, editing?.id);
      if (!alive.current) return;
      setRouters((items) => [item, ...items.filter((r) => r.id !== item.id)]);
      setPage(1);
      setEditing(undefined);
    } catch (e) {
      if (alive.current) setFormError(message(e));
    } finally {
      pending.current = false;
      if (alive.current) setBusy(false);
    }
  };
  const confirm = async () => {
    if (!confirming || pending.current) return;
    const { router, action } = confirming;
    pending.current = true;
    setBusy(true);
    setFormError('');
    try {
      await SiteRouterService[action](projectId, router.id);
      if (!alive.current) return;
      setRouters((items) => items.filter((r) => r.id !== router.id));
      setConfirming(null);
      setPage(1);
    } catch (e) {
      if (alive.current) setFormError(message(e));
    } finally {
      pending.current = false;
      if (alive.current) setBusy(false);
    }
  };
  const openForm = (router: SiteRouter | null) => {
    setForm(
      router
        ? {
            serial_number: router.serial_number,
            mac_address: router.mac_address,
            in_use: router.in_use,
            note: router.note ?? '',
          }
        : { ...empty },
    );
    setFormError('');
    setEditing(router);
  };
  const used = routers.filter((r) => r.in_use).length;
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        flexWrap="wrap"
      >
        <Typography variant="subtitle1" fontWeight={700}>
          라우터 사용 현황
        </Typography>
        {canEdit && (
          <Button variant="outlined" onClick={() => openForm(null)} disabled={loading || !!error}>
            라우터 등록
          </Button>
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        현장에 배정된 장비의 사용 여부를 직접 관리합니다. 실시간 통신 상태와는 다릅니다.
      </Typography>
      {loading ? (
        <Box sx={{ p: 2 }}>
          <CircularProgress size={24} aria-label="라우터 조회 중" />
        </Box>
      ) : error ? (
        <Alert
          severity="error"
          action={<Button onClick={() => setReload((v) => v + 1)}>다시 시도</Button>}
        >
          {error}
        </Alert>
      ) : (
        <>
          <Typography sx={{ my: 1.5 }}>
            사용 중 {used}대 · 미사용 {routers.length - used}대 · 전체 {routers.length}대
          </Typography>
          {routers.length === 0 && (
            <Typography color="text.secondary">등록된 라우터가 없습니다.</Typography>
          )}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(2, minmax(0, 1fr))',
                xl: 'repeat(3, minmax(0, 1fr))',
              },
              gap: 1,
            }}
          >
            {routers.slice((page - 1) * 6, page * 6).map((router) => (
              <Paper key={router.id} variant="outlined" sx={{ p: 1.5, minWidth: 0 }}>
                <Stack direction="row" justifyContent="space-between" gap={1} alignItems="center">
                  <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                    시리얼: {router.serial_number}
                  </Typography>
                  <Chip
                    size="small"
                    label={router.in_use ? '사용 중' : '미사용'}
                    color={router.in_use ? 'success' : 'default'}
                  />
                </Stack>
                <Typography
                  variant="body2"
                  sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere', mt: 1 }}
                >
                  MAC: {router.mac_address}
                </Typography>
                {router.note && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ overflowWrap: 'anywhere', whiteSpace: 'pre-wrap', mt: 1 }}
                  >
                    {router.note}
                  </Typography>
                )}
                {canEdit && (
                  <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
                    <Button
                      onClick={() => openForm(router)}
                      aria-label={`${router.serial_number} 수정`}
                    >
                      수정 / 상태 변경
                    </Button>
                    <Button
                      color="warning"
                      onClick={() => {
                        setFormError('');
                        setConfirming({ router, action: 'release' });
                      }}
                      aria-label={`${router.serial_number} 해지`}
                    >
                      해지
                    </Button>
                    <Button
                      color="error"
                      onClick={() => {
                        setFormError('');
                        setConfirming({ router, action: 'delete' });
                      }}
                      aria-label={`${router.serial_number} 삭제`}
                    >
                      삭제
                    </Button>
                  </Stack>
                )}
              </Paper>
            ))}
          </Box>
          {routers.length > 6 && (
            <Pagination
              count={Math.ceil(routers.length / 6)}
              page={page}
              onChange={(_, value) => setPage(value)}
              sx={{ mt: 2 }}
            />
          )}
        </>
      )}
      <Dialog
        open={editing !== undefined}
        onClose={() => {
          if (!busy) setEditing(undefined);
        }}
        fullWidth
        maxWidth="sm"
      >
        <Box component="form" onSubmit={save}>
          <DialogTitle>{editing ? '라우터 수정' : '라우터 등록'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              {formError && <Alert severity="error">{formError}</Alert>}
              <TextField
                label="시리얼 번호"
                required
                fullWidth
                value={form.serial_number}
                disabled={busy}
                inputProps={{ maxLength: 128 }}
                onChange={(e) => setForm({ ...form, serial_number: e.target.value })}
              />
              <TextField
                label="MAC 주소"
                required
                fullWidth
                value={form.mac_address}
                disabled={busy}
                placeholder="AA:BB:CC:DD:EE:FF"
                onChange={(e) => setForm({ ...form, mac_address: e.target.value })}
                helperText="콜론(:) 또는 하이픈(-)으로 구분한 6자리 쌍"
              />
              <TextField
                label="설명 (선택)"
                fullWidth
                multiline
                minRows={2}
                value={form.note}
                disabled={busy}
                placeholder="설치 위치, 회선 정보, 인수인계 사항 등"
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                helperText={`현장에서 기억해야 할 내용을 자유롭게 적어 주세요 (${form.note.length}/500)`}
                error={form.note.length > 500}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.in_use}
                    disabled={busy}
                    onChange={(_, checked) => setForm({ ...form, in_use: checked })}
                  />
                }
                label={form.in_use ? '사용 중' : '미사용'}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button disabled={busy} onClick={() => setEditing(undefined)}>
              취소
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={busy || !form.serial_number.trim() || !form.mac_address.trim()}
            >
              {busy ? '저장 중…' : '저장'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
      <Dialog
        open={!!confirming}
        onClose={() => {
          if (!busy) setConfirming(null);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{confirming?.action === 'release' ? '라우터 해지' : '라우터 삭제'}</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error">{formError}</Alert>}시리얼{' '}
          {confirming?.router.serial_number}{' '}
          {confirming?.action === 'release'
            ? '라우터를 해지하시겠습니까? 현장 목록에서 빠지고 회사 보관(미사용)으로 남습니다. 다른 현장에서 같은 시리얼 또는 MAC 으로 등록하면 다시 배정됩니다.'
            : '라우터를 삭제하시겠습니까? 기록이 완전히 지워집니다. 장비를 회사에 보관하려면 해지하세요.'}
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setConfirming(null)}>
            취소
          </Button>
          <Button
            disabled={busy}
            color={confirming?.action === 'release' ? 'warning' : 'error'}
            onClick={confirm}
          >
            {confirming?.action === 'release' ? '해지' : '삭제'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

// 현장 변경 시 이전 현장의 목록과 열려 있던 편집 폼을 함께 초기화한다.
const RoutersWidget: React.FC<RoutersWidgetProps> = (props) => (
  <RouterInventory key={props.projectId} {...props} />
);
export default RoutersWidget;
